import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { ExpertPayoutRequest } from "@shopexperts/legacy-api";

interface UseExpertPayoutRequestsOptions {
  actingOrgId: MaybeRef<number | null | undefined>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useExpertPayoutRequests(options: UseExpertPayoutRequestsOptions) {
  const api = useApi();
  const perPage = options.perPage ?? 20;
  const actingOrgId = computed(() => unref(options.actingOrgId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(actingOrgId.value));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["expert", "wallet", actingOrgId.value, "payout-requests", { perPage }]),
    queryFn: async ({ pageParam }) => {
      if (!isPositiveId(actingOrgId.value)) {
        throw new Error("actingOrgId is required to load expert payout requests");
      }

      return await api.expert.wallet.listPayoutRequests({
        query: {
          acting_org_id: actingOrgId.value,
          per_page: perPage,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const payoutRequests = computed<ExpertPayoutRequest[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    payoutRequests,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
